import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FiCalendar, FiCheck, FiCheckCircle, FiGlobe, FiSend, FiTarget, FiTrendingUp, FiUser, FiUsers } from 'react-icons/fi';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import apiService from '../services/api';

const ClientIntakeForm = () => {
  const [formData, setFormData] = useState({
    // Contact Information
    fullName: '',
    companyName: '',
    industry: '',
    businessSize: '',
    email: '',
    phone: '',
    preferredContact: '',

    // Business Goals
    businessPhase: '',
    strategicGoals: '',
    servicesNeeded: [],
    
    // Technology Landscape
    currentTools: '',
    preferredTech: '',
    integrationSystems: '',
    
    // Timeline
    startDate: '',
    launchDate: '',
    
    // Success Criteria
    successDefinition: '',
    keyKPIs: '',
    
    // Optional Interests
    valueAddInterests: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const businessPhases = [
    'Ideation',
    'Market FIT Analysis',
    'Minimum Viable Product (MVP)',
    'Prototype/Mock Ups POC',
    'Implementation',
    'Scale up'
  ];

  const businessSizes = [
    'Startup (1-10 employees)',
    'Small Business (11-50 employees)',
    'Medium Business (51-250 employees)',
    'Large Enterprise (250+ employees)'
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Financial Services',
    'Life Sciences',
    'Manufacturing',
    'Retail/E-commerce',
    'Education',
    'Government',
    'Other'
  ];

  const contactMethods = [
    'Email',
    'Phone',
    'Video Call',
    'In-Person Meeting'
  ];

  const servicesOptions = [
    'Custom Full Stack Application Development (End to End)',
    'Website Design or Redesign & Development & Maintenance',
    'AI/ML Development or Integration',
    'Project/Program Management Strategy',
    'Agile Guidance & Kick Start by Certified Agile Coaches',
    'Cybersecurity & Risk Consulting',
    'Implementing Chat Bots',
    'IT Staffing Support',
    'Business Process Management & Automation',
    'AI Adoption Framework',
    'Business Transformation Strategy',
    'Business Analysis & Documentation',
    'Proof of Concept (POC)',
    'Minimum Viable Product (MVP)',
    'Application Prototype or Mock Up or Wire Frames',
    'Capture Business Requirements to Technical Requirements',
    'Ideation to GTM Roadmap Guidance'
  ];

  const timelineOptions = [
    'Immediately',
    '1–2 months',
    '3–6 months',
    'Flexible'
  ];

  const valueAddOptions = [
    'AI Adoption Framework Templates',
    'Change Management & Digital Transformation Roadmaps',
    'Cybersecurity Gap Analysis',
    'MVP or Prototype Design Sprints',
    'Staff Augmentation or Dedicated Teams',
    'Business Impact Analysis Models'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked 
        ? [...prev[fieldName], value]
        : prev[fieldName].filter(item => item !== value)
    }));
  };

  // Helper function to get character count display
  const getCharCountDisplay = (text, maxLength) => {
    const current = text ? text.length : 0;
    const color = current > maxLength ? 'text-red-500' : current > maxLength * 0.9 ? 'text-yellow-500' : 'text-gray-500';
    return (
      <span className={`text-xs ${color} mt-1 block`}>
        {current}/{maxLength} characters
      </span>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Log form data for debugging
    console.log('Form submission started with data:', formData);

    // Enhanced client-side validation
    const errors = [];

    // Required field validation
    const requiredFields = {
      fullName: 'Full Name',
      companyName: 'Company Name',
      industry: 'Industry/Sector',
      businessSize: 'Business Size',
      email: 'Email Address',
      businessPhase: 'Business Phase',
      strategicGoals: 'Strategic Goals',
      startDate: 'Start Date',
      successDefinition: 'Success Definition'
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field] || formData[field].trim() === '') {
        errors.push(`${label} is required`);
      }
    }

    // Name format validation (letters, spaces, hyphens, dots, apostrophes only)
    if (formData.fullName) {
      const nameRegex = /^[a-zA-Z\s\-\.\']+$/;
      if (!nameRegex.test(formData.fullName.trim())) {
        errors.push('Full name can only contain letters, spaces, hyphens, dots, and apostrophes');
      }
      if (formData.fullName.trim().length < 2 || formData.fullName.trim().length > 100) {
        errors.push('Full name must be between 2 and 100 characters');
      }
    }

    // Company name length validation
    if (formData.companyName && (formData.companyName.trim().length < 2 || formData.companyName.trim().length > 200)) {
      errors.push('Company name must be between 2 and 200 characters');
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email.trim())) {
      errors.push('Please provide a valid email address');
    }

    // Text field length validation
    if (formData.strategicGoals && (formData.strategicGoals.trim().length < 10 || formData.strategicGoals.trim().length > 2000)) {
      errors.push('Strategic goals must be between 10 and 2000 characters');
    }

    if (formData.successDefinition && (formData.successDefinition.trim().length < 10 || formData.successDefinition.trim().length > 2000)) {
      errors.push('Success definition must be between 10 and 2000 characters');
    }

    // Optional field length validation
    if (formData.currentTools && formData.currentTools.trim().length > 2000) {
      errors.push('Current tools description cannot exceed 2000 characters');
    }
    if (formData.preferredTech && formData.preferredTech.trim().length > 2000) {
      errors.push('Preferred tech description cannot exceed 2000 characters');
    }
    if (formData.integrationSystems && formData.integrationSystems.trim().length > 2000) {
      errors.push('Integration systems description cannot exceed 2000 characters');
    }
    if (formData.launchDate && formData.launchDate.trim().length > 500) {
      errors.push('Launch date cannot exceed 500 characters');
    }
    if (formData.keyKPIs && formData.keyKPIs.trim().length > 2000) {
      errors.push('Key KPIs cannot exceed 2000 characters');
    }

    if (errors.length > 0) {
      console.error('Validation errors:', errors);
      alert(`Please fix the following issues:\n\n• ${errors.join('\n• ')}`);
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('Submitting form data to API...');
      
      // Submit to the client intake API endpoint (no timestamp needed)
      const result = await apiService.submitClientIntakeForm(formData);

      console.log('Form submission successful:', result);
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          fullName: '',
          companyName: '',
          industry: '',
          businessSize: '',
          email: '',
          phone: '',
          preferredContact: '',
          businessPhase: '',
          strategicGoals: '',
          servicesNeeded: [],
          currentTools: '',
          preferredTech: '',
          integrationSystems: '',
          startDate: '',
          launchDate: '',
          successDefinition: '',
          keyKPIs: '',
          valueAddInterests: []
        });
        setSubmitStatus(null);
      }, 3000);

    } catch (error) {
      console.error('Form submission error:', {
        message: error.message,
        stack: error.stack,
        formData: formData
      });
      
      // Show user-friendly error message
      let errorMessage = 'There was an error submitting your form:\n\n';
      errorMessage += error.message;
      errorMessage += '\n\nPlease review your entries and try again.';
      
      alert(errorMessage);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <Navbar />
        <div className="pt-28 pb-16 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-12 shadow-2xl border border-gray-100 text-center max-w-2xl mx-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
              className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <FiCheckCircle className="w-10 h-10 text-white" />
            </motion.div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4 font-display tracking-tight">
              Thank You!
            </h2>
            <p className="text-lg text-gray-600 mb-6 font-body leading-relaxed">
              Your client intake form has been submitted successfully. Our team will review your information and get back to you within 24 hours.
            </p>
            <motion.button
              onClick={() => setSubmitStatus(null)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 font-accent tracking-wide"
            >
              Submit Another Form
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide">
              Get Started
            </span>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight font-display tracking-tight text-gray-900 mb-6">
              Client
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Intake Form
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Help us understand your business needs and goals so we can provide you with the most effective solutions tailored to your requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
          >
            {/* Contact Information Section */}
            <div className="p-8 md:p-12 border-b border-gray-100">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <FiUser className="text-white" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 font-display tracking-tight">Contact Information</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">Company Name *</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="Enter your company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">Industry/Sector *</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                  >
                    <option value="">Select your industry</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">Business Size *</label>
                  <select
                    name="businessSize"
                    value={formData.businessSize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                  >
                    <option value="">Select business size</option>
                    {businessSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="your.email@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">Preferred Method of Contact</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {contactMethods.map(method => (
                      <label key={method} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferredContact"
                          value={method}
                          checked={formData.preferredContact === method}
                          onChange={handleInputChange}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 font-body">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Business Goals Section */}
            <div className="p-8 md:p-12 border-b border-gray-100">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                  <FiTarget className="text-white" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 font-display tracking-tight">Business Goals & Strategic Objectives</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">Business Phase *</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {businessPhases.map(phase => (
                      <label key={phase} className="flex items-center space-x-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors duration-300">
                        <input
                          type="radio"
                          name="businessPhase"
                          value={phase}
                          checked={formData.businessPhase === phase}
                          onChange={handleInputChange}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 font-body">{phase}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">
                    What are your top 1-3 strategic business goals this year? *
                  </label>
                  <p className="text-sm text-gray-500 mb-3 font-body">e.g., Scale Revenue, Improve Operational efficiency, Enter new markets, etc.</p>
                  <textarea
                    name="strategicGoals"
                    value={formData.strategicGoals}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="Describe your top strategic business goals..."
                  />
                  {getCharCountDisplay(formData.strategicGoals, 2000)}
                </div>
              </div>
            </div>

            {/* Services Section */}
            <div className="p-8 md:p-12 border-b border-gray-100">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <FiCheck className="text-white" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 font-display tracking-tight">Services Needed</h2>
              </div>

              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700 font-heading">Are you looking for... (Check all that apply)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {servicesOptions.map(service => (
                    <label key={service} className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors duration-300">
                      <input
                        type="checkbox"
                        value={service}
                        checked={formData.servicesNeeded.includes(service)}
                        onChange={(e) => handleCheckboxChange(e, 'servicesNeeded')}
                        className="mt-1 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 font-body leading-relaxed">{service}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Technology Landscape Section */}
            <div className="p-8 md:p-12 border-b border-gray-100">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <FiGlobe className="text-white" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 font-display tracking-tight">Technology Landscape</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">
                    What tools, platforms, or systems are you currently using?
                  </label>
                  <p className="text-sm text-gray-500 mb-3 font-body">e.g., AWS, Azure, Salesforce, Python, React, Oracle, SharePoint</p>
                  <textarea
                    name="currentTools"
                    value={formData.currentTools}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="List your current technology stack..."
                  />
                  {getCharCountDisplay(formData.currentTools, 2000)}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">
                    Are there specific technologies or frameworks you prefer or want to avoid?
                  </label>
                  <textarea
                    name="preferredTech"
                    value={formData.preferredTech}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="Describe your technology preferences..."
                  />
                  {getCharCountDisplay(formData.preferredTech, 2000)}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">
                    Are there systems we'll need to integrate with?
                  </label>
                  <p className="text-sm text-gray-500 mb-3 font-body">e.g., ERP, CRM, payment gateways</p>
                  <textarea
                    name="integrationSystems"
                    value={formData.integrationSystems}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="List systems that need integration..."
                  />
                  {getCharCountDisplay(formData.integrationSystems, 2000)}
                </div>
              </div>
            </div>

            {/* Timeline Section */}
            <div className="p-8 md:p-12 border-b border-gray-100">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                  <FiCalendar className="text-white" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 font-display tracking-tight">Timeline & Engagement Readiness</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">What is your desired project start date? *</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {timelineOptions.map(option => (
                      <label key={option} className="flex items-center space-x-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors duration-300">
                        <input
                          type="radio"
                          name="startDate"
                          value={option}
                          checked={formData.startDate === option}
                          onChange={handleInputChange}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 font-body">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">
                    Do you have a required launch or delivery date?
                  </label>
                  <input
                    type="text"
                    name="launchDate"
                    value={formData.launchDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="If yes, please specify the date or time frame"
                  />
                  {getCharCountDisplay(formData.launchDate, 500)}
                </div>
              </div>
            </div>

            {/* Success Criteria Section */}
            <div className="p-8 md:p-12 border-b border-gray-100">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <FiTrendingUp className="text-white" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 font-display tracking-tight">Impact Measurement & Success Criteria</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">
                    How will you define success for this project or engagement? *
                  </label>
                  <p className="text-sm text-gray-500 mb-3 font-body">e.g., Uptime improvement, reduced processing time, increased revenue, security compliance</p>
                  <textarea
                    name="successDefinition"
                    value={formData.successDefinition}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="Describe how you'll measure project success..."
                  />
                  {getCharCountDisplay(formData.successDefinition, 2000)}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-heading">
                    Are there any key KPIs or metrics you need to meet?
                  </label>
                  <textarea
                    name="keyKPIs"
                    value={formData.keyKPIs}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                    placeholder="List any specific KPIs or metrics..."
                  />
                  {getCharCountDisplay(formData.keyKPIs, 2000)}
                </div>
              </div>
            </div>

            {/* Optional Value-Add Section */}
            <div className="p-8 md:p-12">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
                  <FiUsers className="text-white" size={24} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 font-display tracking-tight">Optional Value-Add Interests</h2>
              </div>

              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700 font-heading">Would you like to receive more information on:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {valueAddOptions.map(option => (
                    <label key={option} className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors duration-300">
                      <input
                        type="checkbox"
                        value={option}
                        checked={formData.valueAddInterests.includes(option)}
                        onChange={(e) => handleCheckboxChange(e, 'valueAddInterests')}
                        className="mt-1 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 font-body leading-relaxed">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 md:p-12">
              <div className="text-center">
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                  className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-12 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-accent tracking-wide flex items-center space-x-2 mx-auto"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <FiSend />
                      <span>Submit Client Intake Form</span>
                    </>
                  )}
                </motion.button>

                {submitStatus === 'error' && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 text-red-600 font-body"
                  >
                    There was an error submitting your form. Please try again.
                  </motion.p>
                )}

                <p className="mt-6 text-sm text-gray-600 font-body">
                  By submitting this form, you agree to our terms of service and privacy policy. 
                  We'll use this information to better understand your needs and provide you with tailored solutions.
                </p>
              </div>
            </div>
          </motion.form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClientIntakeForm; 